import { Component, DestroyRef, inject, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { FormWrapperComponent } from '../../../ui/form-wrapper/form-wrapper.component';
import { InputFieldComponent } from '../../../ui/inputField/inputField.component';
import { BkSelectComponent } from '../../../inputs/bk-select/bk-select.component';
import { FormBuilder, ReactiveFormsModule, Validators, FormArray } from '@angular/forms';
import { MessageAttributeMenuComponent } from '../../../ui/message-attribute-menu/message-attribute-menu.component';
import { MessageAttributeChipsComponent } from '../../../ui/message-attribute-chips/message-attribute-chips.component';
import { MessagesService } from '../../../../services/campaign/messages.service';
import { CampaignStepperService } from '../../../../services/campaign/campaign-stepper.service';
import { ProcessCampaignService } from '../../../../services/campaign/process-campaign.service';
import { UtilityService } from '../../../../services/utility.service';
import { CancelCampaignService } from '../../../../services/campaign/cancel-campaign.service';
import { MessageTemplate } from '../../../../services/message-templates/message.template';
import { removeMessageAttribute } from '../../../../../utils/utils';
import { SmsParts, describeChar, formatCount, smsParts } from '../../../../../utils/sms';
import { BroadcastMessageService } from '../../../../services/broadcast/broadcast.service';
import { ISenderID } from '../../../../services/broadcast/types/broadcastTypes';
import { WizardAudienceService } from '../../../../pages/organization/campaigns/create-campaign/wizard-audience.service';

@Component({
  selector: 'app-message-form',
  standalone: true,
  imports: [
    FormWrapperComponent,
    InputFieldComponent,
    BkSelectComponent,
    ReactiveFormsModule,
    MessageAttributeMenuComponent,
    MessageAttributeChipsComponent,
  ],
  templateUrl: './message-form.component.html',
  styleUrl: '../campaign-form.css',
})
export class MessageFormComponent implements OnDestroy {
  constructor(private fb: FormBuilder) {}

  messagesService = inject(MessagesService);
  campaignStepperService = inject(CampaignStepperService);
  processCampaignService = inject(ProcessCampaignService);
  cancelCampaignService = inject(CancelCampaignService);
  utility = inject(UtilityService);
  templateService = inject(MessageTemplate);
  broadcastService = inject(BroadcastMessageService);
  audience = inject(WizardAudienceService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);
  templates: any = [];
  senderIDs: ISenderID[] = [];
  private timeoutId: ReturnType<typeof setTimeout> | undefined;

  readonly formatCount = formatCount;

  messagesForm = this.fb.group({
    senderId: ['', Validators.required],
    subject: ['', Validators.required],
    body: ['', Validators.required],
    template: '',
    hashtags: this.fb.array([]),
    hashtag: '',
  });

  get hashtags() {
    return this.messagesForm.get('hashtags') as FormArray;
  }

  /** Length, parts and encoding of the copy as the network counts it. */
  get parts(): SmsParts {
    return smsParts(this.messagesForm.value.body as string);
  }

  /**
   * What the screen-reader note says about the cost. It changes only with the
   * part count or encoding, so it is not read out on every keystroke.
   */
  get partsAnnouncement(): string {
    const { parts, encoding } = this.parts;
    if (!parts) return '';
    return `${parts} SMS ${parts === 1 ? 'part' : 'parts'}${encoding === 'Unicode' ? ', sent as Unicode' : ''}`;
  }

  /** Only Unicode needs a note: it is the case that quietly raises cost. */
  get unicodeNote(): string {
    const { encoding, offender } = this.parts;
    if (encoding !== 'Unicode') return '';
    const cause = offender ? `because of ${describeChar(offender)}` : 'because of a special character';
    return `Sent as Unicode ${cause}, so each part holds 70 characters instead of 160.`;
  }

  /** What still stops "Add message" from working. */
  get missingText(): string {
    const value = this.messagesForm.value;
    const missing = [
      !value.senderId && 'a sender ID',
      !value.subject && 'a subject',
      !value.body && 'the message text',
    ].filter(Boolean) as string[];
    if (!missing.length) return '';
    const list = missing.length > 1 ? `${missing.slice(0, -1).join(', ')} and ${missing[missing.length - 1]}` : missing[0];
    return `Add ${list} to save this message.`;
  }

  /** Typed copy that has not been added to the campaign yet. */
  get hasUnsavedDraft(): boolean {
    const value = this.messagesForm.value;
    return !!(value.subject || value.body);
  }

  addHashtag(hashtag: string) {
    this.hashtags.push(this.fb.control(hashtag));
  }

  removeHashtag(index: number) {
    this.hashtags.removeAt(index);
    this.messagesService.removeHashtag(index);
  }

  getHashtag(event: Event) {
    let hashtag = (event.target as HTMLInputElement).value.replace(/\s+/g, '');
    this.addHashtag(hashtag);
    this.messagesService.getHashtag(hashtag);
    this.messagesForm.controls['hashtag'].setValue('');
  }

  appendAttribute(textarea: HTMLTextAreaElement, attribute: string) {
    const template = `{{${attribute}}}`;

    const start = textarea.selectionStart; // cursor start
    const end = textarea.selectionEnd; // cursor end (same if no selection)

    // Build new string: before + template + after
    const messageBody = this.messagesForm.controls['body'].value || '';
    const before = messageBody.substring(0, start);
    const after = messageBody.substring(end);
    this.messagesForm.controls['body'].setValue(before + template + after);

    // Move cursor to the end of the inserted text
    const newPos = start + template.length;

    // DOM update is async – use setTimeout (or requestAnimationFrame)
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
    this.timeoutId = setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newPos, newPos);
      this.timeoutId = undefined;
    }, 0);
  }

  // remove a contact-attribute placeholder from the message body (chip dismiss)
  removeAttribute(attribute: string) {
    this.messagesForm.controls['body'].setValue(
      removeMessageAttribute(this.messagesForm.controls['body'].value, attribute)
    );
  }

  handleSaveMessage() {
    if (this.messagesForm.invalid) return;
    const sender = this.messagesForm.value.senderId || '';
    this.messagesService.savedMessages.push({
      id: this.utility.randomId(),
      subject: this.messagesForm.value.subject,
      body: this.messagesForm.value.body,
      hashtags: this.messagesForm.value.hashtags,
      senderId: sender,
    });
    // The next message usually comes from the same sender, so it stays chosen.
    this.messagesForm.reset({
      senderId: sender,
      subject: '',
      body: '',
      template: '',
      hashtags: [],
      hashtag: '',
    });
    this.hashtags.clear();
    this.messagesService.reset();
    this.messagesService.messages.senderId = sender;
    this.validateForm();
  }

  /** Continue needs at least one added message. Read live: the list beside the form can remove them. */
  get invalid(): boolean {
    return this.messagesService.savedMessages.length < 1;
  }

  validateForm() {
    // A step that no longer holds up is not done: later steps wait again.
    if (this.invalid) {
      const step = this.campaignStepperService.steps.find((item) => item.slug === 'messages');
      if (step) step.completed = false;
    }
  }

  handleSubmit() {
    this.validateForm();
    if (this.invalid) {
      return;
    } else {
      this.processCampaignService.messages = this.messagesForm.value;
      this.campaignStepperService.stepCompleted('messages');
      this.campaignStepperService.moveToNextStep('messages');
    }
  }

  goBack() {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { step: 'recipients' },
      queryParamsHandling: 'merge',
    });
  }

  /** The picker hands over the template id; its text replaces the message body. */
  handleSelectTemplate(templateId: string) {
    const selectedTemplate = this.templates.find((item: any) => String(item.id) === templateId);
    if (!selectedTemplate) return;

    this.messagesService.messages.body = selectedTemplate.content;
    this.messagesService.messages.template = selectedTemplate.name;

    this.messagesForm.patchValue({
      body: selectedTemplate.content,
    });
  }

  ngOnInit() {
    // Keep what was typed when the user opened the preview before adding the message.
    if (this.messagesService.messages.body) {
      this.messagesForm.patchValue({
        subject: this.messagesService.messages.subject,
        body: this.messagesService.messages.body,
        senderId: this.messagesService.messages.senderId,
      });

      if (this.messagesService.messages.hashtags.length > 0) {
        this.hashtags.clear();
        this.messagesService.messages.hashtags.forEach((hashtag: string) => {
          this.hashtags.push(this.fb.control(hashtag));
        });
      }
    } else if (this.messagesService.messages.senderId) {
      this.messagesForm.patchValue({ senderId: this.messagesService.messages.senderId });
    }
    this.validateForm();
    this.audience.refresh();

    // The preview beside the form follows every keystroke, not just blur.
    this.messagesForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      this.messagesService.messages.subject = value.subject ?? '';
      this.messagesService.messages.body = value.body ?? '';
      this.messagesService.messages.senderId = value.senderId ?? '';
    });

    // fetch the templates
    this.templateService.fetchTemplates();
    this.templateService.getTemplates.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      this.templates = data;
    });

    // fetch sender IDs
    this.broadcastService.fetchSenderIDs().subscribe();
    this.broadcastService.getSenderIDs$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (value) => {
        this.senderIDs = value;
      },
    });
  }

  ngOnDestroy() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
  }
}
